import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { IMenuItem } from 'app/store/models/page.model';
import { EReducers, IReducers } from 'app/store/reducers';
import { IUserSessionData, UserState, UserType } from 'app/store/models/user.model';
import { Store } from '@ngrx/store';
import {
  IMessageWSTopicData,
  MESSAGE_WS_ACTION_UNLOCK,
  MESSAGES_WS_TOPIC,
} from '../../../../store/models/message.model';
import {
  logout,
  toggleEmailPreview,
  toggleHints,
  toggleUnsubscribe,
  toggleVipFeature,
  vmsMessageLock,
} from 'app/store/actions/user.action';
import { WebSocketService } from 'app/store/services/web-socket.service';
import { Router } from '@angular/router';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { SessionService } from '../../../../store/services/session.service';
import { DiscountsService } from '../../../../store/services/discounts.service';
import { MobileService } from '../../../../store/services/mobile.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AccountService } from '../../../../store/services/account.service';
import * as moment from 'moment';
import { EAccountReportStatus } from '../../../../store/models/account.model';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { IReportEvent, REPORT_EVENT_NAME } from '../../../../reports/list-reports/list-reports.component';
import { EventService } from '../../../../store/services/event.service';

const getIntegrationLocation = (name: string) => {
  return name === 'Fast Signs' ? 'fastsignsdatasync' : 'bpdatasync';
};

@Component({
  selector: 'app-authenticated-header-menu',
  templateUrl: './authenticated-header-menu.component.html',
  styleUrls: ['./authenticated-header-menu.component.scss'],
})
export class AuthenticatedHeaderMenuComponent implements OnInit, OnDestroy {

  @Input() menuItems: IMenuItem[];

  @ViewChild('element') element: ElementRef<HTMLUListElement>;

  private vsmLockMessageId: number;
  private reportDownloadInProgress: boolean;

  private lastWindowTop: number;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private isMobileView: boolean;

  private _bounceReportActive: boolean;
  private _unsubscribeButtonActive: boolean;
  private userState: UserState;

  constructor(
    private readonly store: Store<IReducers>,
    private readonly wsService: WebSocketService,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly discountService: DiscountsService,
    private readonly mobileService: MobileService,
    private readonly accountService: AccountService,
    private readonly eventService: EventService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.mobileService.observe()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isMobile => this.isMobileView = isMobile);
  }

  @HostListener('window:scroll') // for window scroll events
  onScroll() {
    if (!this.isMobileView) {
      return;
    }
    const st = window.pageYOffset || document.documentElement.scrollTop;

    if (Math.abs(st - this.lastWindowTop) <= 40) {
      // some back and forth happening while moving the mouse wheel
      this.lastWindowTop = st;
      return;
    }

    if (st > this.lastWindowTop) {
      this.element.nativeElement.classList.remove('d-flex');
      this.element.nativeElement.classList.add('d-none');
    } else {
      this.element.nativeElement.classList.remove('d-none');
      this.element.nativeElement.classList.add('d-flex');
    }
    this.lastWindowTop = st;
  }

  ngOnInit(): void {
    this.store.select(EReducers.USER).subscribe((userState: UserState) => {
      this.userState = userState;
      this._unsubscribeButtonActive = this.userState.updateInProgress;
      this._bounceReportActive = this.userState.updateInProgress;
      if (userState.sessionData && userState.loggedIn) {
        this.setMenuItems(userState.sessionData);
        this.vsmLockMessageId = userState.vsmLockMessageId;
      }
      const reportInProgressItem = localStorage.getItem(REPORT_EVENT_NAME);

      if (reportInProgressItem) {
        const reportData = JSON.parse(reportInProgressItem);
        if (reportData.pushTo === this.userState.sessionData.id) {
          this.reportDownloadInProgress = true;
        }

        this.eventService.on(REPORT_EVENT_NAME)
          .pipe(takeUntil(this.destroy$))
          .subscribe(payload => {
            if (typeof payload !== 'boolean') {
              return;
            }

            this.reportDownloadInProgress = payload;
            this.setMenuItems(this.userState.sessionData);
            this.changeDetectorRef.detectChanges();
          });
      }
    });
  }

  setMenuItems(userSession: IUserSessionData) {
    this.menuItems = [];
    this.menuItems.push({
      label: 'Inbox',
      route: '/messages',
      icon: 'Inbox Top Nav Icon',
    });
    const myAccountItems: IMenuItem[] = [];

    const tokenData: { generated_by?: string } = JSON.parse(localStorage.getItem('token'));

    if (tokenData.generated_by) {
      myAccountItems.push({
        label: 'Move to your account',
        onClick: () => {
          if (this.vsmLockMessageId) {
            this.wsService.send<IMessageWSTopicData>({
              topic: MESSAGES_WS_TOPIC,
              data: {
                action: MESSAGE_WS_ACTION_UNLOCK,
                message_id: this.vsmLockMessageId,
              },
            });
            this.store.dispatch(vmsMessageLock({
              messageId: null,
            }));
          }

          const oldTokens: any[] = JSON.parse(
            localStorage.getItem('oldTokens'),
          );

          if (oldTokens && oldTokens.length) {
            const lastToken = oldTokens[oldTokens.length - 1];
            localStorage.setItem('token', JSON.stringify(lastToken));
            oldTokens.splice(oldTokens.length - 1, 1);

            localStorage.setItem('oldTokens', JSON.stringify(oldTokens));
            window.location.reload();
            return;
          }
          const t: { generated_by?: string, access_token?: string } = JSON.parse(localStorage.getItem('token'));

          if (!t) {
            window.location.reload();
            return;
          }
          if (!t.generated_by) {
            return;
          }
          t.access_token = t.generated_by;
          delete t.generated_by;
          localStorage.setItem('token', JSON.stringify(t));
          window.location.reload();
        },
      });
    }

    let manageMyAccountItems: IMenuItem[] = [];

    manageMyAccountItems.push({
      label: 'Change Passcode',
      route: '/accounts/change_password',
      icon: 'Change Passcode Icon',
    });

    if (userSession.api_keys_feature_enabled) {
      manageMyAccountItems.push({
        label: 'API Keys',
        route: '/accounts/api_keys',
        icon: 'Change Passcode Icon',
      });
    }

    if (userSession.user_type !== UserType.VSM
      && userSession.billing_designation !== 'quickbooks'
      && userSession.billing_designation !== 'no_billing') {
      manageMyAccountItems.push({
        label: 'Update Credit Card',
        onClick: () => {
          this.router.navigate(['/accounts/update_credit_card']);
        },
        icon: 'Update Card Icon',
      });
    }
    manageMyAccountItems = [
      ...manageMyAccountItems,
      {
        label: 'Update Personal Info',
        route: '/accounts/update_personal_info',
        icon: 'Update Personal Info Icon',
      },
      {
        label: 'Select Timezone',
        route: '/accounts/change_timezone',
        icon: 'Select Timezone Icon',
      },
    ];

    if (userSession.user_type === UserType.SINGLE_USER || userSession.user_type === UserType.MANAGER) {
      manageMyAccountItems.push({
        label: 'Set SMS details',
        route: '/accounts/set_sms_details',
        icon: 'Dog Icon',
      });
    }

    if (userSession.user_type === UserType.MASTER && false) {
      manageMyAccountItems.push({
        label: 'Manage spams',
        route: '/accounts/manage_spams',
        // icon: faCommentDots
      });
    }

    if (userSession.user_type !== UserType.VSM && userSession.user_type !== UserType.FRANCHISE) {
      if (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1)) {
        manageMyAccountItems.push({
          label: 'Set Variables',
          route: '/accounts/set_variables',
          icon: 'Set Variables Icon',
        });
      }
    }

    myAccountItems.push({
      label: 'Manage',
      children: manageMyAccountItems,
      icon: 'Manage Icon',
    });

    let featuresMenuItems: IMenuItem[] = [];

    featuresMenuItems.push({
      label: `Email Preview`,
      onClick: () => {
        this.store.dispatch(
          toggleEmailPreview({
            account_id: userSession.id,
            current: userSession.emails_preview,
          }),
        );
      },
      hasToggle: true,
      toggleValue: userSession.emails_preview,
      icon: 'Contact Icon',
    });

    if ([UserType.VSM, UserType.PARTNER, UserType.FRANCHISE].indexOf(userSession.user_type) === -1) {
      if (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1)) {
        featuresMenuItems.push({
          label: 'Pipeline Feature',
          route: '/pipelines',
          icon: 'Pipeline Feature Icon',
        });
      }
    }

    if (
      userSession.user_type !== UserType.MANAGER &&
      userSession.user_type !== UserType.PARTNER &&
      userSession.user_type !== UserType.FRANCHISE
    ) {
      if (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1)) {
        featuresMenuItems.push({
          label: `VIP Feature`,
          onClick: () =>
            this.store.dispatch(
              toggleVipFeature({
                value: !userSession.vip_mode,
                account_id: userSession.id,
              }),
            ),
          icon: 'VIP Feature Icon',
          hasToggle: true,
          toggleValue: userSession.vip_mode,
        });
      }
      // featuresMenuItems.push({
      //     label: 'Zoho Sync',
      //     route: '/accounts/zoho_sync'
      // });

      if (userSession.user_type !== UserType.VSM && userSession.user_type !== UserType.MASTER) {
        featuresMenuItems.push({
          label: 'Products',
          route: '/products',
          icon: 'Products Icon',
        });
      }
    }

    if (userSession.sales_force_connected) {
      featuresMenuItems = [
        ...featuresMenuItems,
        {
          label:
            'Salesforce <img alt="Edit Entry" height="10" id="img8" src="/assets/images/greencheck.png" width="10">DEACTIVATE',
          route: '/sales_connect/disconnect_sales',
          asHTML: true,
          // icon: faBalanceScale
        },
        {
          label:
            'Salesforce <img alt="Edit Entry" height="10" id="img8" src="/assets/images/greencheck.png" width="10">ACTIVATE',
          route: '/sales_connect/new',
          asHTML: true,
          // icon: faBalanceScaleRight
        },
      ];
    }

    if (userSession.inbox_management_settings) {
      featuresMenuItems.push({
        label: `Inbox Management`,
        route: '/accounts/inbox_management',
        hasToggle: true,
        toggleValue: userSession.inbox_management_settings.on,
        icon: 'Inbox Management Icon',
      });
    }

    if (!!userSession.integration && userSession.integration.id) {
      featuresMenuItems.push({
        label: `${userSession.integration.name} Data Integration`,
        route: this.sessionService.isImpersonating() ? `/accounts/integrations/${userSession.integration.id}` : `https://click2sell.formstack.com/forms/${getIntegrationLocation(userSession.integration.name)}`,
      });
    }

    myAccountItems.push({
      label: 'Features',
      children: featuresMenuItems,
      icon: 'Features Icon',
    });
    let reportMenuItems: IMenuItem[] = [];

    if ([UserType.MANAGER, UserType.PARTNER, UserType.VSM].indexOf(userSession.user_type) === -1) {
      reportMenuItems.push({
        label: 'Bounce Report',
        onClick: () => {
          if (this._bounceReportActive) {
            return;
          }

          this._bounceReportActive = true;
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: 'Downloading Bounced report...',
                type: 'success',
              },
            },
          }));
          this.accountService.downloadBouncedReport(userSession.id)
            .subscribe(
              response => {
                const dataType = response.type;
                const binaryData = [];
                binaryData.push(response);
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(
                  new Blob(binaryData, { type: dataType }),
                );

                downloadLink.setAttribute(
                  'download',
                  `bounced_${moment().format('YYYY-MM-DD')}.csv`,
                );
                document.body.appendChild(downloadLink);
                downloadLink.click();
                this._bounceReportActive = false;
              },
              error => {
                error.error.text().then(blobError => {
                  this._bounceReportActive = false;
                  const bError = JSON.parse(blobError);
                  this.store.dispatch(
                    openSnackbar({
                      snackbarData: {
                        data: {
                          type: 'error',
                          message: bError.message.join('\n'),
                        },
                      },
                    }),
                  );
                });
              },
            );
        },
      });

      if (userSession.roles && userSession.roles.length > 0) {
        reportMenuItems.push({
          label: 'Email Activity Report',
          route: '/account/bounced_csv',
        });
      }

      if (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1)) {
        reportMenuItems = [
          ...reportMenuItems,
          {
            label: 'Set Sales Goals',
            route: '/accounts/prefs_goals',
            icon: 'Set Sales Goals Icon',
          },
        ];
      }

      if (userSession.user_type === UserType.MANAGER) {
        reportMenuItems.push({
          label: 'Report Recipients',
          route: '/account/rep_recipient',
        });
      }
    }

    if (userSession.user_type !== UserType.VSM && userSession.user_type !== UserType.MASTER && !this.reportDownloadInProgress) {
      if (userSession.reports > EAccountReportStatus.DISABLED) {
        if (userSession.reports === EAccountReportStatus.CONTACTS_ONLY) {
          reportMenuItems.push({
            label: 'Download Contacts Report',
            onClick: () => this.downloadContactsReport(EAccountReportStatus.CONTACTS_ONLY, userSession.id),
          });
        } else if (userSession.reports === EAccountReportStatus.CONTACTS_AND_CLOSER) {
          reportMenuItems.push({
            label: 'Download Contacts Report',
            onClick: () => this.downloadContactsReport(EAccountReportStatus.CONTACTS_ONLY, userSession.id),
          });
          reportMenuItems.push({
            label: 'Download Closer Report',
            onClick: () => this.downloadContactsReport(EAccountReportStatus.CLOSER_ONLY, userSession.id),
          });
        } else if (userSession.reports === EAccountReportStatus.CLOSER_ONLY) {
          reportMenuItems.push({
            label: 'Download Closer Report',
            onClick: () => this.downloadContactsReport(EAccountReportStatus.CLOSER_ONLY, userSession.id),
          });
        }
      }
    }

    if (reportMenuItems.length) {
      myAccountItems.push({
        label: 'Reports',
        children: reportMenuItems,
        icon: 'Reports Icon',
      });
    }

    const viewReceiptMenuItems: IMenuItem[] = [];
    if (userSession.invoices && userSession.invoices.length > 0
    ) {
      if (userSession.invoices && userSession.invoices.length > 0) {
        if ([UserType.SINGLE_USER, UserType.MANAGER, UserType.MASTER].indexOf(userSession.user_type) !== -1) {
          viewReceiptMenuItems.push({
            label: 'View all',
            route: '/accounts/invoices',
          });
        }
        for (const invoice of userSession.invoices) {
          viewReceiptMenuItems.push({
            label: invoice.charge_date,
            onClick: () => this.discountService.downloadInvoice(invoice.id)
              .subscribe((res: HttpResponse<Blob>) => {
                const url = window.URL.createObjectURL(res.body);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${invoice.charge_date}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }, error => {
              }),
          });
        }
      }
    } else {
      viewReceiptMenuItems.push({
        label: 'No receipts',
        onClick: () => console.log('No receipts'),
      });
    }

    
    if (
      userSession.user_type !== UserType.FRANCHISE &&
      (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1))
    ) {
      myAccountItems.push({
        label: `View Receipt`,
        children: viewReceiptMenuItems,
        icon: 'Cheat Sheet Icon',
      });
    }

    if (
      userSession.user_type !== UserType.MANAGER &&
      userSession.user_type !== UserType.PARTNER &&
      userSession.user_type !== UserType.FRANCHISE
    ) {

      if (userSession.user_type !== UserType.MASTER || (userSession.user_type === UserType.MASTER && userSession.id === 1)) {
        myAccountItems.push({
          label: 'Set BCC address',
          children: [
            {
              label: 'All Dispositions',
              route: '/accounts/bcc-manage/all',
              icon: 'Dog Icon',
            },
            {
              label: 'Meeting',
              route: '/accounts/bcc-manage/meeting',
              icon: 'Dog Icon',
            },
            {
              label: 'Follow up',
              route: '/accounts/bcc-manage/followup',
              icon: 'Dog Icon',
            },
            {
              label: 'Cycle',
              route: '/accounts/bcc-manage/cycle',
              icon: 'Dog Icon',
            },
            {
              label: 'Remove',
              route: '/accounts/bcc-manage/remove',
              icon: 'Dog Icon',
            },
            {
              label: 'Inactive',
              route: '/accounts/bcc-manage/inactive',
              icon: 'Dog Icon',
            },
          ],
          icon: 'Set BCC Address Icon',
        });
      }
    }

    if (
      userSession.user_type &&
      userSession.user_type === UserType.SINGLE_USER
    ) {
      myAccountItems.push({
        label: `Unsubscribe`,
        onClick: () => this.toggleUnsubscribe(userSession),
        hasToggle: true,
        toggleValue: userSession.unsubscribe,
        icon: 'Unsubscribe Icon',
      });
    }

    this.menuItems.push({
      label: 'My Account',
      children: myAccountItems,
      icon: 'My Account Icon',
    });

    this.menuItems.push({
      label: 'Sign out',
      onClick: () => {
        this.sessionService.destroySession();
        this.store.dispatch(logout());
      },
      icon: 'Sign Out Icon',
    });

    let supportMenuItems: IMenuItem[] = [
      // no need for now
      // {
      //     label: 'Contact',
      //     route: '/contact_us',
      //     icon: 'Contact Icon'
      // }
    ];

    if (
      userSession.confirmed &&
      !userSession.skip_guide &&
      userSession.guide_progress < 5 &&
      userSession.roles &&
      userSession.roles.length > 0
    ) {
      supportMenuItems.push({
        label: 'Show Wizard',
        onClick: () => console.log('Show the guide'),
        // icon: faMagic
      });
    }

    if (userSession.confirmed) {
      supportMenuItems.push({
        label: 'Knowledge Base',
        route: 'https://www.retreva.com/knowledge-base/',
        icon: 'Training Video Icon',
      });
    }

    supportMenuItems = [
      ...supportMenuItems,
      {
        label: 'CheatSheet',
        onClick: () =>
          window.open('/assets/pdf/C2S_User_Help_Matrix.jpg', '_blank'),
        icon: 'Cheat Sheet Icon',
      },
      {
        label: `Hint Pop Ups`,
        onClick: () =>
          this.store.dispatch(
            toggleHints({
              value: !userSession.popup_tips,
              account_id: userSession.id,
            }),
          ),
        hasToggle: true,
        toggleValue: userSession.popup_tips,
        icon: 'Hint Popups Icon',
      },
      {
        label: 'Open Ticket',
        onClick: () =>
          window.open('/assets/static/helpcenter.html', '_blank'),
        icon: 'Help Center Icon',
      },
    ];

    this.menuItems.push({
      label: 'Support',
      children: supportMenuItems,
      icon: 'Support Icon',
    });
  }

  downloadContactsReport(reportType: EAccountReportStatus, accountId: number) {
    this.reportDownloadInProgress = true;
    this.store.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: 'Your report is being downloading...',
          type: 'success',
        },
      },
    }));
    this.accountService.downloadContactsReport(reportType, [accountId], true, 'csv').subscribe((response: HttpResponse<Blob>) => {
      const fileName = response.headers.get('Content-Disposition').split(';').map(f => f.trim()).filter(f => f.indexOf('filename=') !== -1)[0].split('=')[1].replace(new RegExp('"', 'g'), '');
      const url = window.URL.createObjectURL(response.body);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.trim();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, err => {
      this.reportDownloadInProgress = false;
      if (err instanceof HttpErrorResponse && err.error.code > 1005) {
        this.store.dispatch(openSnackbar({
          snackbarData: {
            data: {
              message: 'Your report is being created. Please, wait for this report link to be downloaded',
              type: 'error',
            }
          }
        }));
        const reportData: any = {
          completed: false,
          file: null,
          pushTo: this.userState.sessionData.id,
          keyId: err.error.code - 1005,
        };
        localStorage.setItem(REPORT_EVENT_NAME, JSON.stringify(reportData));
        this.eventService.broadcast<IReportEvent>(REPORT_EVENT_NAME, reportData);
        this.reportDownloadInProgress = true;
        return;
      }
      this.store.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: err.error,
            type: 'error',
          },
        },
      }));
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  private toggleUnsubscribe(userSession: IUserSessionData) {
    if (this._unsubscribeButtonActive) {
      return;
    }
    this._unsubscribeButtonActive = true;
    if (!userSession.unsubscribe) {
      this.store.dispatch(
        toggleUnsubscribe({
          account_id: userSession.id,
          value: !userSession.unsubscribe,
        }),
      );
      return;
    }

    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Warning',
          content:
            'Warning you are about turn the Unsubscribe link OFF. Recipients will not longer be able to unsubscribe by clicking a link at the bottom of your emails. Recipients can still ask to be unsubscribed and you can easily remove them by using the Remove replay action. If this is what you want to do click "OK" to complete the action. If not, click "Close""',
          buttons: [
            {
              onClick: dialog => {
                this.store.dispatch(
                  toggleUnsubscribe({
                    account_id: userSession.id,
                    value: !userSession.unsubscribe,
                  }),
                );
                dialog.closeDialog();
              },
              color: ButtonColor.BLUE,
              size: ButtonSize.NORMAL,
              text: 'OK',
            },
          ],
          canClose: true,
        },
      }),
    );
  }

}
